"use client";

import { useState, useEffect } from "react";
import { ShoppingCart, Users, MessageCircle, Award, TrendingUp, Target } from "lucide-react";
import logoHorizontal from "@/assets/logo-horizontal.png";
import { WHATSAPP_CONFIG, HOTMART_CONFIG } from "@/config/constants";

const Linktree = () => {
  const [mounted, setMounted] = useState(false);
  const [count, setCount] = useState(0);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    const target = 1200;
    const duration = 2000;
    const increment = target / (duration / 16);
    let current = 0;

    const interval = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(interval);
      } else {
        setCount(Math.floor(current));
      }
    }, 16);

    return () => clearInterval(interval);
  }, [mounted]);

  if (!mounted) return null;

  const links = [
    {
      id: 1,
      title: "Método Libertação Das Apostas",
      subtitle: "MÉTODO",
      description: "A chave pra retomar o controle da sua vida e sair das apostas",
      cta: "Começar agora",
      icon: ShoppingCart,
      href: HOTMART_CONFIG.metodoLda,
      gradient: "from-blue-600 to-cyan-500",
      delay: "delay-100",
      isPrimary: true,
    },
    {
      id: 2,
      title: "Jornada da Transformação em 8 Pilares",
      subtitle: "JORNADA",
      description: "Trilhas práticas nos 8 pilares pra evoluir mente, rotina e comportamento longe das apostas",
      cta: "Quero começar a jornada",
      icon: Award,
      href: HOTMART_CONFIG.jornada8Pilares,
      gradient: "from-purple-600 to-pink-500",
      delay: "delay-200",
      isPrimary: false,
    },
    {
      id: 3,
      title: "Mentoria Individual",
      subtitle: "1:1",
      description: "Sessões individuais pra quem precisa de ajuda mais próxima, direcionada pro seu caso",
      cta: "Agendar uma sessão",
      icon: MessageCircle,
      href: WHATSAPP_CONFIG.mentoriaLink,
      gradient: "from-blue-500 to-cyan-600",
      delay: "delay-300",
      isPrimary: false,
    },
    {
      id: 4,
      title: "Grupo WhatsApp",
      subtitle: "COMUNIDADE",
      description: "Comunidade exclusiva no WhatsApp com apoio diário, lembretes e motivação pra não voltar às apostas",
      cta: "Entrar no grupo",
      icon: Users,
      href: WHATSAPP_CONFIG.groupLink,
      gradient: "from-green-500 to-emerald-600",
      delay: "delay-400",
      isPrimary: false,
    },
  ];

  return (
    <main className="relative min-h-screen w-full overflow-hidden">
      {/* Background animado premium */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-primary">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary/30 rounded-full blur-[100px] motion-safe:animate-glow-pulse" aria-hidden />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent/20 rounded-full blur-[100px] motion-safe:animate-glow-pulse" style={{ animationDelay: "2s" }} aria-hidden />
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-purple-600/20 rounded-full blur-[100px] motion-safe:animate-glow-pulse" style={{ animationDelay: "4s" }} aria-hidden />
      </div>

      {/* Grid background */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.03] [mask-image:radial-gradient(70%_70%_at_50%_50%,black,transparent)]"
        style={{
          backgroundImage:
            "linear-gradient(to right, hsl(var(--border)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border)) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      {/* Conteúdo */}
      <div className="relative z-10 w-full min-h-screen flex flex-col items-center justify-center px-4 py-16 sm:py-20">
        <div className="w-full max-w-md">
          {/* Header Premium */}
          <div className="mb-14 text-center space-y-6 motion-safe:animate-fade-in">
            {/* Logo Horizontal */}
            <div className="flex justify-center motion-safe:animate-fade-in" style={{ animationDelay: "100ms" }}>
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-accent rounded-lg blur-lg opacity-40" />
                <img
                  src={logoHorizontal}
                  alt="LDA - Libertação Das Apostas"
                  className="relative h-16 w-auto object-contain"
                />
              </div>
            </div>

            {/* Headline */}
            <div className="space-y-3 motion-safe:animate-fade-in" style={{ animationDelay: "200ms" }}>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black leading-tight">
                Escolha seu próximo passo
                <br />
                <span className="bg-gradient-accent bg-clip-text text-transparent drop-shadow-[0_0_18px_rgba(100,192,242,0.6)]">
                  para sair das apostas hoje
                </span>
              </h1>
              <p className="text-base text-muted-foreground max-w-sm mx-auto leading-relaxed">
                Selecione a jornada que mais combina com o seu momento e comece sua transformação agora.
              </p>
            </div>


            {/* Prova Social */}
            <div className="text-center text-sm text-muted-foreground motion-safe:animate-fade-in" style={{ animationDelay: "250ms" }}>
              <p className="font-semibold text-accent">
                Mais de <span className="text-lg font-black">{count.toLocaleString("pt-BR")}</span> pessoas já passaram pelos nossos métodos de libertação das apostas.
              </p>
            </div>
          </div>

          {/* Links Container */}
          <div className="space-y-4 motion-safe:animate-scale-in" style={{ animationDelay: "400ms" }}>
            {links.map((link, index) => {
              const Icon = link.icon;
              return (
                <a
                  key={link.id}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group relative block motion-safe:animate-fade-in ${link.delay}`}
                  style={{ animationDelay: `${400 + index * 100}ms` }}
                >
                  {/* Glow background */}
                  <div className={`absolute inset-0 -z-10 bg-gradient-to-r ${link.gradient} blur-lg opacity-0 group-hover:opacity-75 transition-opacity duration-500 rounded-2xl`} />

                  {/* Card Container */}
                  <div className={`relative rounded-2xl overflow-hidden p-5 sm:p-6 backdrop-blur-xl transition-all duration-500 ${
                    link.isPrimary
                      ? "bg-gradient-to-br from-card/60 to-card/40 border-2 border-accent/50 shadow-[0_0_40px_rgba(100,192,242,0.3)] hover:shadow-[0_0_80px_rgba(100,192,242,0.5)]"
                      : "bg-card/40 border border-white/10 hover:border-accent/50 hover:shadow-[0_0_60px_rgba(100,192,242,0.2)]"
                  }`}>
                    {/* Glow interno para card principal */}
                    {link.isPrimary && (
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
                        background: "radial-gradient(600px 600px at 50% 0%, hsl(199, 89%, 48%, 0.15), transparent 80%)"
                      }} />
                    )}

                    <div className="relative flex items-start gap-4">
                      {/* Icon Container */}
                      <div className={`flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br ${link.gradient} p-3 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 pt-0.5">
                        {link.subtitle && (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {link.subtitle}
                          </span>
                        )}
                        <h3 className="font-bold text-foreground text-base sm:text-lg group-hover:text-accent transition-colors line-clamp-2">
                          {link.title}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {link.description}
                        </p>
                        {link.cta && (
                          <p className="text-xs font-semibold text-accent mt-2 line-clamp-1">
                            → {link.cta}
                          </p>
                        )}
                      </div>

                      {/* Arrow */}
                      <div className="flex-shrink-0 pt-1">
                        <svg className="w-5 h-5 text-accent group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </div>
                    </div>

                    {/* Hover effect line */}
                    <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-transparent via-accent to-transparent w-0 group-hover:w-full transition-all duration-500" />
                  </div>
                </a>
              );
            })}
          </div>

          {/* Footer Premium */}
          <div className="mt-14 pt-8 border-t border-white/10 text-center motion-safe:animate-fade-in" style={{ animationDelay: "800ms" }}>
            <div className="space-y-4">

              <p className="text-sm text-muted-foreground">
                Dúvidas? Fale com nosso suporte no WhatsApp
              </p>
              <a
                href={WHATSAPP_CONFIG.supportLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors font-semibold group"
              >
                <MessageCircle className="w-5 h-5 group-hover:scale-110 transition-transform" />
                WhatsApp
              </a>

              {/* Additional Trust Elements */}
              <div className="mt-6 text-[11px] text-muted-foreground space-y-1">
                <p className="flex items-center justify-center gap-2">
                  <TrendingUp className="w-3 h-3 text-emerald-500" />
                  Acesso imediato após a compra
                </p>
                <p className="flex items-center justify-center gap-2">
                  <Target className="w-3 h-3 text-emerald-500" />
                  Suporte completo da comunidade
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes glow-pulse {
          0%, 100% {
            opacity: 0.5;
          }
          50% {
            opacity: 0.8;
          }
        }

        .motion-safe\:animate-fade-in {
          animation: fade-in 0.6s ease-out forwards;
          opacity: 0;
        }

        .motion-safe\:animate-scale-in {
          animation: scale-in 0.5s ease-out;
        }

        .motion-safe\:animate-glow-pulse {
          animation: glow-pulse 4s ease-in-out infinite;
        }

        .reduced-motion .motion-safe\:animate-glow-pulse,
        .reduced-motion .motion-safe\:animate-fade-in,
        .reduced-motion .motion-safe\:animate-scale-in {
          animation: none;
          opacity: 1;
          transform: none;
        }
      `}</style>
    </main>
  );
};

export default Linktree;
